import type { Viewport } from '@livecontrol/core-ui';
import { Device } from '@livecontrol/core-ui';
import { Str } from '@livecontrol/core-utils';
import _ from 'lodash';
import type { ReactElement } from 'react';
import { Fragment } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Store, useSettingsModal } from '../../store';

interface Props {
  viewport: Viewport;
}

const Toggle = styled(Dropdown.Toggle)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 5px 6px 5px 10px !important;  
  background: transparent;
  border: 1px solid transparent !important; 
  border-radius: 11px;
  transition: all var(--transition);

  &::after {
    content: none;
  }

  &:hover,
  &:focus {
    box-shadow: none;
  }

  &[aria-expanded='true'] {
    border: 1px solid rgba(255, 255, 255, 0.12) !important; 
    background: rgba(255, 255, 255, 0.06);
  }

  &[aria-expanded='true'] .fa-angle-down {
    transform: rotate(180deg);
  }
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
  text-align: right;

  .email {
    color: var(--white);
    font-size: 13px;
    font-weight: 600;
  }

  .meta {
    color: var(--white60);
    font-size: 12px;
    font-weight: 400;
  }
`;

const Avatar = styled.img`
  width: 38px;
  height: 38px;
  object-fit: cover;
  border: 1px solid rgba(120, 130, 255, 0.3);
`;

const Caret = styled.i`
  color: var(--white60);
  font-size: 14px;
  transition: transform var(--transition);
`;

const Menu = styled(Dropdown.Menu)`
  width: 248px;
  padding: 0;
  margin-top: 8px;
  background: rgb(13, 23, 51);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 13px;
  box-shadow: rgba(0, 0, 0, 0.5) 0px 18px 44px;
  overflow: hidden;
`;

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
`;

const HeaderAvatar = styled.img`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  object-fit: cover;
  border: 1px solid rgba(120, 130, 255, 0.3);
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;

  .name,
  .email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .name {
    color: var(--white);
    font-size: 14px;
    font-weight: 700;
  }

  .email {
    color: var(--white60);
    font-size: 12.5px;
    font-weight: 400;
  }
`;

const Body = styled.div`
  padding: 6px;
`;

const Item = styled(Dropdown.Item)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 11px;
  border-radius: 8px;
  color: var(--white);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;

  i {
    width: 18px;
    text-align: center;
    font-size: 15px;
    color: var(--white);
  }

  &:hover,
  &:focus,
  &:active {
    background: rgba(255, 255, 255, 0.06);
    color: var(--white);
  }

  &.danger {
    color: var(--coral);

    i {
      color: var(--coral);
    }

    &:hover,
    &:focus,
    &:active {
      background: rgba(255, 90, 95, 0.1);
      color: var(--coral);
    }
  }
`;

const Separator = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 6px 5px;
`;

/* An item either navigates or does something in place, like opening Settings. */
type MenuItem = {
  key: string;
  label: string;
  icon: string;
  danger?: boolean;
} & ({ onSelect: () => void } | { route: string });

export const User = ({ viewport }: Props): ReactElement => {
  const history = useHistory();
  const me = Store.User.useMe();
  const isMobile = Device.isMobile(viewport);  

  const name = _.compact([me.first_name, me.last_name]).join(' ');
  const avatar = me.profile_picture ?? '/assets/avatar.png';

  const openSettings = useSettingsModal((v) => v.setOpen);

  const access = me.commandCenterAccess;

  /*
   * Menu items grouped into sections; sections are rendered with a separator
   * between them. Each item redirects to its route.
   */
  const sections: MenuItem[][] = [];

  if (access?.event_access) {
    sections.push([
      { key: 'upcoming', label: 'Upcoming events', icon: 'fal fa-calendar', route: '/events/upcoming' },
      { key: 'past', label: 'Past events', icon: 'fal fa-history', route: '/events/past' }
    ]);
  }

  const management: MenuItem[] = [];

  if (access?.device_access) {
    management.push({ key: 'manager', label: 'Device Manager', icon: 'fal fa-video', route: '/manager' });
  }

  if (access?.installer_management) {
    management.push({ key: 'installers', label: 'Installers', icon: 'fal fa-download', route: '/installers' });
  }

  if (management.length > 0) {
    sections.push(management);
  }

  sections.push([
    {
      key: 'settings',
      label: 'Settings',
      icon: 'fal fa-sliders-h',
      onSelect: (): void => {
        openSettings(true);
      }
    },
    { key: 'logout', label: 'Log out', icon: 'fal fa-sign-out-alt', route: '/logout', danger: true }
  ]);

  return (
    <Dropdown alignRight>
      <Toggle forwardedAs='button' className='x-pseudo'>
        {!isMobile && (
          <Identity>
            {me.email && <span className='email'>{Str.truncate(me.email)}</span>}
            {/*
              * Name only: the account's time_zone used to show here, but every
              * time we render is in the BROWSER's zone (see the events toolbar),
              * so a profile zone that didn't match just read as a contradiction.
              */}
            <span className='meta'>{name && Str.truncate(name)}</span>
          </Identity>
        )}

        <Avatar
          className='rounded-circle'
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          src={me.profile_picture ?? '/assets/avatar.png'}
          alt={name || 'User'}
        />

        <Caret className='fas fa-angle-down' />
      </Toggle>

      <Menu>
        <MenuHeader>
          <HeaderAvatar className='rounded-circle' src={avatar} alt={name || 'User'} />
          <HeaderInfo>
            <span className='name'>{name || 'User'}</span>
            {me.email && <span className='email'>{me.email}</span>}
          </HeaderInfo>
        </MenuHeader>

        <Body>
          {sections.map((items, index) => (
            <Fragment key={items[0].key}>
              {index > 0 && <Separator />}

              {items.map((item) => (
                <Item
                  key={item.key}
                  className={item.danger ? 'danger' : undefined}
                  onClick={(): void => {
                    if ('route' in item) {
                      history.push(item.route);
                    } else {
                      item.onSelect();
                    }
                  }}
                >
                  <i className={item.icon} />
                  {item.label}
                </Item>
              ))}
            </Fragment>
          ))}
        </Body>
      </Menu>
    </Dropdown>
  );
};
